import { FC, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { ProductSearchQueryParameter } from "@/types/ProductSearchQueryParameter";
import SearchScafold from "@/components/SearchScafold";
import { InputType, SearchConditionGroupResponse } from "@/web-client";
import { SelectionProps } from "@/interfaces/SelectionProps";
import { SearchConditionGroupPullDownOnSp } from "@/features/search_product/components/SearchConditionGroupPullDownOnSp";
import { SearchConditionGroupCheckboxOnSp } from "@/features/search_product/components/SearchConditionGroupCheckboxOnSp";

export type SearchConditionFormField = Pick<
  ProductSearchQueryParameter,
  "search_condition_value_ids" | "category_id"
>;

type Props = {
  searchConditionGroup: SearchConditionGroupResponse;
} & SelectionProps<SearchConditionFormField>;

export const SearchConditionSelection: FC<Props> = ({
  defaultValue: { search_condition_value_ids },
  searchConditionGroup,
  onUpdate,
  onBack,
}): JSX.Element => {
  const { handleSubmit, getValues, setValue, watch } = useForm<{
    search_condition_value_ids: string;
  }>({
    defaultValues: {
      search_condition_value_ids,
    },
  });

  // フォームに入っている値
  const watchingSearchConditionValueIds = watch("search_condition_value_ids");

  // フォームに入っている値を配列に変換
  const selectedIds = useMemo(
    () =>
      watchingSearchConditionValueIds
        ? watchingSearchConditionValueIds.split(",").map((v) => Number(v))
        : [],
    [watchingSearchConditionValueIds],
  );

  // セレクトによって変更された値
  const changeSelectHandler = useCallback(
    (
      to: { value: number; label: string },
      from: { value: number; label: string },
    ) => {
      // 現在選択中の値を取得
      const currentValue = getValues("search_condition_value_ids");

      // 選択なしの場合、選択されている項目を削除
      const filtered = currentValue
        ? currentValue
            .split(",")
            .filter((id) => Number(id) !== from?.value)
            .map((v) => Number(v))
        : [];

      // 選択なし（to.value === 0）でなければ、選択された項目のvalueを配列に追加する
      const newSelectedIds = to.value ? [...filtered, to.value] : filtered;

      // 値の更新
      setValue("search_condition_value_ids", newSelectedIds.join(","));
    },
    [getValues, setValue],
  );

  const changeCheckboxHandler = useCallback(
    ({ checked, value }) => {
      const all = checked
        ? [...selectedIds, value]
        : selectedIds.filter((id) => id !== value);

      // 重複を削除
      const uniqueIds = Array.from(new Set(all));

      // 値の更新
      setValue("search_condition_value_ids", uniqueIds.join(","));
    },
    [setValue, selectedIds],
  );

  return (
    <SearchScafold
      title={searchConditionGroup.label}
      handleSubmit={handleSubmit((data) => {
        onUpdate(data);
        onBack();
      })}
    >
      <div className="p-20 flex flex-col space-y-8 text-xs">
        {
          <>
            {searchConditionGroup.input_type === InputType.SELECT && (
              <SearchConditionGroupPullDownOnSp
                searchConditionGroup={searchConditionGroup}
                selectedIds={selectedIds}
                onChange={changeSelectHandler}
              />
            )}

            {searchConditionGroup.input_type === InputType.CHECKBOX && (
              <SearchConditionGroupCheckboxOnSp
                searchConditionGroup={searchConditionGroup}
                selectedIds={selectedIds}
                onChange={changeCheckboxHandler}
              />
            )}
          </>
        }
      </div>
    </SearchScafold>
  );
};
